/**
 * Repeating blocks — "every weekday 09:00 standup".
 *
 * A recurrence is a rule; entries are written when a day is opened. Changing a
 * rule therefore affects days you have not visited yet, and leaves already
 * materialised days as they are.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Repeat, Plus, Trash2, Power } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { TimeField } from "@/components/time-field";
import { formatMinute, formatDuration, todayKey } from "@/lib/dates";
import {
  KIND_ORDER,
  KINDS,
  DURATION_PRESETS,
  type DashplanKind,
} from "./constants";

/** Monday-first labels; `value` is the JS day number the schema stores. */
const DAYS = [
  { value: 1, short: "Mon" },
  { value: 2, short: "Tue" },
  { value: 3, short: "Wed" },
  { value: 4, short: "Thu" },
  { value: 5, short: "Fri" },
  { value: 6, short: "Sat" },
  { value: 0, short: "Sun" },
];

const WEEKDAYS = [1, 2, 3, 4, 5];

export function RecurrencesDialog({
  open,
  onOpenChange,
  date,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string;
}) {
  const { toast } = useToast();
  const recurrences = useQuery(
    api.dashplanRecurrences.list,
    open ? { includeInactive: true } : "skip",
  );
  const create = useMutation(api.dashplanRecurrences.create);
  const update = useMutation(api.dashplanRecurrences.update);
  const remove = useMutation(api.dashplanRecurrences.remove);

  const [title, setTitle] = useState("");
  const [startMinute, setStartMinute] = useState(9 * 60);
  const [duration, setDuration] = useState(30);
  const [kind, setKind] = useState<DashplanKind>("meeting");
  const [days, setDays] = useState<number[]>(WEEKDAYS);
  const [busy, setBusy] = useState(false);

  const toggleDay = (value: number) =>
    setDays((current) =>
      current.includes(value)
        ? current.filter((d) => d !== value)
        : [...current, value],
    );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (days.length === 0) {
      toast({
        title: "Pick at least one day",
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    try {
      await create({
        title,
        kind,
        startMinute,
        durationMinutes: duration,
        daysOfWeek: days,
        // Start from the day being viewed so it does not backfill history.
        startDate: date < todayKey() ? date : todayKey(),
      });
      setTitle("");
      toast({
        title: "Repeating block added",
        description: "It appears on matching days as you open them.",
      });
    } catch (error) {
      toast({
        title: "Could not save",
        description: String(error),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Repeating blocks</DialogTitle>
          <DialogDescription>
            Rules that add a block to matching days automatically. Days you have
            already planned are left alone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {recurrences === undefined && (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          )}
          {recurrences?.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">
              Nothing repeating yet.
            </p>
          )}

          {recurrences?.map((rec) => (
            <div
              key={rec._id}
              className={cn(
                "flex items-center gap-2 rounded-lg border border-border p-3",
                !rec.active && "opacity-50",
              )}
            >
              <Repeat className="w-4 h-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{rec.title}</p>
                <p className="text-xs text-muted-foreground font-mono tabular-nums">
                  {formatMinute(rec.startMinute)} ·{" "}
                  {formatDuration(rec.durationMinutes)} ·{" "}
                  {DAYS.filter((d) => rec.daysOfWeek.includes(d.value))
                    .map((d) => d.short)
                    .join(" ")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label={rec.active ? `Pause ${rec.title}` : `Resume ${rec.title}`}
                onClick={() => update({ id: rec._id, active: !rec.active })}
              >
                <Power className={cn("w-4 h-4", rec.active && "text-primary")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${rec.title}`}
                className="text-destructive hover:text-destructive"
                onClick={() =>
                  remove({
                    id: rec._id,
                    removeFutureEntries: true,
                    fromDate: todayKey(),
                  })
                }
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <form
          onSubmit={submit}
          className="space-y-3 border-t border-border pt-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="rec-title">New repeating block</Label>
            <Input
              id="rec-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Daily standup"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="rec-time">At</Label>
              <TimeField
                id="rec-time"
                value={startMinute}
                onChange={setStartMinute}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-duration">For</Label>
              <Select
                value={String(duration)}
                onValueChange={(v) => setDuration(Number(v))}
              >
                <SelectTrigger id="rec-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_PRESETS.map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {formatDuration(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-kind">Kind</Label>
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as DashplanKind)}
              >
                <SelectTrigger id="rec-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_ORDER.map((k) => (
                    <SelectItem key={k} value={k}>
                      {KINDS[k].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium mb-1.5">On</legend>
            <div className="flex gap-1">
              {DAYS.map((day) => {
                const on = days.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    aria-pressed={on}
                    className={cn(
                      "flex-1 py-1.5 text-xs rounded-md border transition-colors",
                      on
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setDays(WEEKDAYS)}
              >
                Weekdays
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setDays([0, 1, 2, 3, 4, 5, 6])}
              >
                Every day
              </button>
            </div>
          </fieldset>

          <Button
            type="submit"
            className="w-full"
            disabled={busy || !title.trim() || days.length === 0}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add repeating block
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
